import React, { useState } from "react"
import { DevStoreBadge } from "../shared/DevStoreBadge"
import { formatDateTime } from "../transactions/format"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

// Mark/unmark a shop as a development store (doc 06). Excludes its data from
// every financial report (Home, Subscriptions, Transactions) while keeping the
// rows intact — visible here and, if flagged, still available on the Customers
// list behind the "show dev stores" filter. Owner/admin only (canFlag).
export function DevelopmentStoreControl({ merchant, devStorePath, canFlag, copy, onUpdated }) {
  const [noting, setNoting] = useState(false)
  const [note, setNote] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)

  const patch = async (isDev, noteValue) => {
    setSubmitting(true)
    setError(false)
    try {
      const res = await fetch(devStorePath, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({ is_development_store: isDev, note: noteValue }),
      })
      if (!res.ok) throw new Error("update failed")
      const json = await res.json()
      onUpdated(json.merchant)
      setNoting(false)
      setNote("")
    } catch {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  const unmark = () => {
    if (window.confirm(copy.devStore.confirmUnmark)) patch(false, null)
  }

  if (merchant.is_development_store) {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <DevStoreBadge label={copy.devStore.badge} />
          {canFlag && (
            <button
              type="button"
              onClick={unmark}
              disabled={submitting}
              className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {copy.devStore.unmark}
            </button>
          )}
        </div>
        <p className="max-w-xs text-right text-xs text-gray-400 dark:text-gray-500">
          {merchant.development_store_flagged_by &&
            copy.devStore.flaggedBy.replace("%{name}", merchant.development_store_flagged_by)}
          {merchant.development_store_flagged_at &&
            ` · ${copy.devStore.flaggedAt.replace("%{time}", formatDateTime(merchant.development_store_flagged_at))}`}
        </p>
        {merchant.development_store_note && (
          <p className="max-w-xs text-right text-xs italic text-gray-500 dark:text-gray-400">
            "{merchant.development_store_note}"
          </p>
        )}
        {error && <p className="text-xs text-red-600 dark:text-red-400">{copy.devStore.error}</p>}
      </div>
    )
  }

  if (!canFlag) return null

  if (!noting) {
    return (
      <button
        type="button"
        onClick={() => setNoting(true)}
        className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        {copy.devStore.mark}
      </button>
    )
  }

  return (
    <div className="flex w-full max-w-xs flex-col items-end gap-2">
      <label className="w-full text-right text-xs text-gray-500 dark:text-gray-400">{copy.devStore.notePrompt}</label>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={copy.devStore.noteLabel}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-brand-500"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => patch(true, note)}
          disabled={submitting}
          className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {copy.devStore.mark}
        </button>
        <button
          type="button"
          onClick={() => { setNoting(false); setNote("") }}
          className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          {copy.devStore.cancel}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{copy.devStore.error}</p>}
    </div>
  )
}
